import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Observable, of, OperatorFunction } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { NgClass } from '@angular/common';
import { InputRestrictionDirective } from '../../directives/input-restriction.directive';
import { NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule } from '@angular/forms';
import { GeolocationService } from '../../../core/services/geolocation.service';

@Component({
    selector: 'city-suggestion',
    templateUrl: './city-suggestion.component.html',
    styleUrls: ['./city-suggestion.component.scss'],
    imports: [FormsModule, NgbTypeahead, InputRestrictionDirective, NgClass]
})
export class CitySuggestionComponent implements OnInit {

	@Input() basicInfo: any;
	@Input() data: any;
	@Input() parentobj: any;
	@Input() showCityError: boolean = false;
	@Input() Joblocation:boolean=false

	@Output() cityUpdated: EventEmitter<any> = new EventEmitter<any>();

	public city: string = '';
	public predictions: any[] = [];
	public citySelected: boolean = false;
	public cityErrorMessage: string = '';
	public hasToShowCitySuggestions: boolean = false;
	public oldData: any;

	constructor(private geolocationService: GeolocationService) { }

	ngOnInit() {
		if (this.basicInfo) {
			let obj = this.basicInfo;
			this.oldData = this.basicInfo;
			this.city = obj.Location;
		} else if (this.data !== undefined) {
			this.oldData = this.data;
			if (this.data.Location != null && this.data.Location.Locality != null) {
				this.city = this.data.Location.Locality;
			} else if (this.data.city !== undefined && this.data.Location == null) {
				this.city = this.data.city;
			} else {
				this.city = this.data.Location ?? '';
			}
		} else {
			this.city = '';
		}

		this.parentobj.city = this.city;
		this.parentobj.showCityError = false;

		if (!this.city) {
			this.geolocationService.getCityFromIp().subscribe(detectedCity => {
				if (detectedCity && !this.city) {
					this.city = detectedCity;
					this.parentobj.city = detectedCity;
					this.citySelected = true;
					this.parentobj.showCityError = false;
				}
			});
		}
	}

	// suggest cities
	suggestCities() {
		this.validateCity();
		this.citySelected = false;
		// city can arrive as a non-string when the parent passes a Location object,
		// and "".length would then be undefined and silently return no suggestions
		const term = typeof this.city === 'string' ? this.city : '';
		// the Maps script is loaded async in index.html, so a modal opened before it
		// is ready would otherwise throw inside switchMap and kill the typeahead for
		// the life of this component
		if (term.length >= 1 && !this.isPlacesReady()) {
			// no request is even attempted in this state - say so, otherwise it looks
			// identical to "the API returned no matches"
			console.warn('[city-suggestion] Google Places not available - no request sent.',
				{ googleDefined: typeof google !== 'undefined',
				  mapsDefined: typeof google !== 'undefined' && !!(google as any).maps,
				  placesDefined: typeof google !== 'undefined' && !!(google as any).maps?.places });
		}
		if (term.length >= 1 && this.isPlacesReady()) {
			let service = new google.maps.places.AutocompleteService();
			let result = new Observable<string[]>(observer => {
				service.getPlacePredictions({ input: term, types: ['(cities)'] },
					(predictions, status) => {
						if (status == google.maps.places.PlacesServiceStatus.OK) {
							let response = {
								status: 200,
								data: {
									predictions: predictions,
								},
							};
							this.predictions = response.data.predictions.slice(0, 10);
							observer.next(this.predictions);
							observer.complete();
						} else {
							// ZERO_RESULTS is normal; anything else is a real failure that
							// was previously indistinguishable from an empty result set
							if (status !== google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
								console.warn('[city-suggestion] Places request failed:', status);
							}
							this.hasToShowCitySuggestions = true;
							observer.next([]);
							observer.complete();
						}
					});
			});
			return result;
		} else {
			this.hasToShowCitySuggestions = false;
			return of([]);
		}
	}

	/** True once the async Google Maps Places script has finished loading. */
	private isPlacesReady(): boolean {
		return typeof google !== 'undefined'
			&& !!google.maps
			&& !!google.maps.places
			&& !!google.maps.places.AutocompleteService;
	}

	search: OperatorFunction<string, readonly string[]> = (text$: Observable<string>) =>
		text$.pipe(
			debounceTime(200),
			distinctUntilChanged(),
			// catchError inside the switchMap: a failure returns an empty list for
			// that keystroke only, instead of terminating the stream and leaving the
			// field permanently unresponsive
			switchMap(() => this.suggestCities().pipe(catchError(() => of([]))))
		)

	formatter = (result: any) => result.description;

	citySelectedFn(event: any) {
		event.preventDefault();
		let cityName = event.item.description;
		if (cityName !== '') {
			this.city = cityName;
			this.parentobj.city = cityName;
			this.hasToShowCitySuggestions = false;
			this.citySelected = true;
			this.cityUpdated.emit(true);
		} else {
			this.city = '';
			this.parentobj.city = '';
		}
	}

	// city field validation
	private validateCity() {
		if (!this.city || this.city === '') {
			this.cityErrorMessage = 'Please select a City';
			this.showCityError = true;
			this.parentobj.showCityError = true;
		} else {
			this.showCityError = false;
			this.parentobj.showCityError = false;
		}
	}

	//  hide city suggestions
	hideCitySuggestions() {
		this.hasToShowCitySuggestions = false;
		if (!this.citySelected) {
			if (this.oldData) {
				if (this.oldData.Location != null && this.oldData.Location.Locality != null) {
					if (this.oldData.Location.Locality !== this.city) {
						this.city = '';
					} else if (this.oldData.Location !== this.city) {
						this.city = '';
					}
				}
			} else {
				this.city = '';
			}

			this.validateCity();
		}
	}

}
